import { childEndpoints, leadsEndpoints } from "@/config/endpoints";
import axiosInstance from "@/lib/axios";
import { useQuery } from "@tanstack/react-query";

/**
 * Reason returned by `GET /core/leads/:id/visibility` and
 * `GET /core/child/:id/visibility`. Mirrors the server-side
 * `VisibilityReason` interface in
 * `Xale-Server/src/core/leads/lead.visibility.service.ts`.
 *
 * The first reason in the array is the primary one (used as the
 * single-line tooltip / icon label). The full array is shown inside
 * the popover.
 */
export type VisibilityReasonKind =
  | "tenant_admin"
  | "stage_admin"
  | "stage_manage"
  | "custom_field_manage"
  | "personal_assigned"
  | "personal_via_child"
  | "parent_lead_access";

export interface VisibilityReason {
  kind: VisibilityReasonKind;
  label: string;
  detail: string;
  meta?: Record<string, unknown>;
}

interface VisibilityResponse {
  reasons: VisibilityReason[];
}

/**
 * Lazy-loaded visibility explainer for a lead. Don't pre-fetch on the
 * list view — the popover is opt-in, so we only hit the endpoint when
 * the user hovers/clicks the info icon. `enabled` defaults to `false`
 * for that reason.
 */
export function useLeadVisibility(
  leadId: number | string | undefined | null,
  enabled = false,
) {
  return useQuery<VisibilityResponse>({
    queryKey: ["lead-visibility", leadId],
    enabled: enabled && leadId != null,
    queryFn: async () => {
      const res = await axiosInstance.get(
        `${leadsEndpoints}/${leadId}/visibility`,
      );
      return { reasons: res.data?.reasons ?? [] };
    },
  });
}

/** Same shape as {@link useLeadVisibility} but for a single deal/child row. */
export function useChildVisibility(
  childId: number | string | undefined | null,
  enabled = false,
) {
  return useQuery<VisibilityResponse>({
    queryKey: ["child-visibility", childId],
    enabled: enabled && childId != null,
    queryFn: async () => {
      const res = await axiosInstance.get(
        `${childEndpoints}/${childId}/visibility`,
      );
      return { reasons: res.data?.reasons ?? [] };
    },
  });
}
